import React, { useCallback, useMemo, useState } from "react";
import { CircularProgress, Checkbox, FormControlLabel } from "@mui/material";
import { IssueDetailValue } from "../../types/api";

interface StatusInfo {
  displayName: string;
  isCompleted: boolean;
  color?: string;
}

interface IssuesTabProps {
  issues: IssueDetailValue[];
  isLoading: boolean;
  error: string | null;
  onRetry: () => void;
  workspaceId?: string;
  projectId?: string;
  webBaseUrl?: string;
  completedStatusIds: Set<string>;
  statusMap: Map<string, StatusInfo>;
}

interface ParsedIssueValue {
  title?: string;
  operating_result?: string;
}

export const IssuesTab: React.FC<IssuesTabProps> = ({
  issues,
  isLoading,
  error,
  onRetry,
  workspaceId,
  projectId,
  webBaseUrl,
  completedStatusIds,
  statusMap,
}) => {
  // 完了した課題を含むかどうかのstate（デフォルトは含まない）
  const [includeCompleted, setIncludeCompleted] = useState(false);

  // フィルタリングされた課題リスト
  const filteredIssues = useMemo(() => {
    if (includeCompleted) {
      return issues;
    }
    // 完了ステータスの課題を除外
    return issues.filter(
      (issue) => !completedStatusIds.has(issue.issue_status_id)
    );
  }, [issues, includeCompleted, completedStatusIds]);

  // 完了した課題の数
  const completedCount = useMemo(() => {
    return issues.filter((issue) =>
      completedStatusIds.has(issue.issue_status_id)
    ).length;
  }, [issues, completedStatusIds]);

  // valueをパースしてtitleとoperating_resultを取得
  const parseIssueValue = useCallback((value?: string): ParsedIssueValue => {
    if (!value) return {};
    try {
      const parsed = JSON.parse(value);
      return {
        title: parsed.title,
        operating_result: parsed.operating_result,
      };
    } catch {
      console.warn("Issue valueのパースに失敗しました:", value);
      return {};
    }
  }, []);

  // TFACT UIへのリンクを生成
  const getIssueUrl = useCallback(
    (issueId: string): string | null => {
      if (!workspaceId || !projectId || !webBaseUrl) return null;
      return `${webBaseUrl}/${workspaceId}/${projectId}/issues?page=1&t=d_issue&k=${issueId}&dm=d&em=`;
    },
    [workspaceId, projectId, webBaseUrl]
  );

  const formatDate = useCallback((dateString?: string) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("ja-JP", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateString;
    }
  }, []);

  // ステータスの色を取得
  const getStatusColor = useCallback(
    (statusId: string): { bg: string; text: string } => {
      const statusInfo = statusMap.get(statusId);
      if (statusInfo?.isCompleted) {
        return { bg: "#d1fae5", text: "#10b981" };
      }
      // 未完了の場合
      return { bg: "#fef3c7", text: "#d97706" };
    },
    [statusMap]
  );

  if (isLoading) {
    return (
      <div style={{ textAlign: "center", padding: "40px", color: "#6b7280" }}>
        <CircularProgress size={24} style={{ marginRight: "8px" }} />
        関連課題を取得中...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: "center", padding: "40px" }}>
        <div style={{ color: "#ef4444", marginBottom: "16px" }}>{error}</div>
        <button
          onClick={onRetry}
          style={{
            padding: "8px 16px",
            fontSize: "0.8rem",
            background: "#667eea",
            color: "white",
            border: "none",
            borderRadius: "6px",
            cursor: "pointer",
          }}
        >
          再試行
        </button>
      </div>
    );
  }

  if (issues.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: "40px", color: "#6b7280" }}>
        このユースケースに関連する課題は見つかりませんでした。
      </div>
    );
  }

  return (
    <div>
      <div
        style={{
          marginBottom: "12px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div style={{ fontSize: "0.85rem", color: "#6b7280" }}>
          このユースケースに関連する <strong>{filteredIssues.length}</strong>{" "}
          件の課題が見つかりました
          {!includeCompleted && completedCount > 0 && (
            <span style={{ marginLeft: "8px", color: "#9ca3af" }}>
              （完了済み {completedCount} 件を除く）
            </span>
          )}
        </div>
        <FormControlLabel
          control={
            <Checkbox
              checked={includeCompleted}
              onChange={(e) => setIncludeCompleted(e.target.checked)}
              size="small"
              sx={{
                padding: "4px",
                "& .MuiSvgIcon-root": { fontSize: "1rem" },
              }}
            />
          }
          label={
            <span style={{ fontSize: "0.75rem", color: "#6b7280" }}>
              完了した課題を含む
            </span>
          }
          sx={{ marginRight: 0 }}
        />
      </div>
      {filteredIssues.length === 0 ? (
        <div style={{ textAlign: "center", padding: "40px", color: "#6b7280" }}>
          表示する課題がありません。
        </div>
      ) : (
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: "0.8rem",
            marginTop: "8px",
          }}
        >
          <thead>
            <tr>
              <th
                style={{
                  backgroundColor: "#f8f9fa",
                  border: "1px solid #e5e7eb",
                  padding: "8px 12px",
                  textAlign: "left",
                  fontWeight: 600,
                  color: "#374151",
                  fontSize: "0.75rem",
                  width: "8%",
                }}
              >
                課題ID
              </th>
              <th
                style={{
                  backgroundColor: "#f8f9fa",
                  border: "1px solid #e5e7eb",
                  padding: "8px 12px",
                  textAlign: "left",
                  fontWeight: 600,
                  color: "#374151",
                  fontSize: "0.75rem",
                  width: "22%",
                }}
              >
                タイトル
              </th>
              <th
                style={{
                  backgroundColor: "#f8f9fa",
                  border: "1px solid #e5e7eb",
                  padding: "8px 12px",
                  textAlign: "left",
                  fontWeight: 600,
                  color: "#374151",
                  fontSize: "0.75rem",
                  width: "35%",
                }}
              >
                操作結果
              </th>
              <th
                style={{
                  backgroundColor: "#f8f9fa",
                  border: "1px solid #e5e7eb",
                  padding: "8px 12px",
                  textAlign: "left",
                  fontWeight: 600,
                  color: "#374151",
                  fontSize: "0.75rem",
                  width: "10%",
                }}
              >
                ステータス
              </th>
              <th
                style={{
                  backgroundColor: "#f8f9fa",
                  border: "1px solid #e5e7eb",
                  padding: "8px 12px",
                  textAlign: "left",
                  fontWeight: 600,
                  color: "#374151",
                  fontSize: "0.75rem",
                  width: "15%",
                }}
              >
                更新日時
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredIssues.map((issue, index) => {
              const parsedValue = parseIssueValue(issue.value);
              const issueUrl = getIssueUrl(issue.issue_id);
              const isCompleted = completedStatusIds.has(issue.issue_status_id);
              const statusInfo = statusMap.get(issue.issue_status_id);
              const statusColor = getStatusColor(issue.issue_status_id);

              return (
                <tr
                  key={`${issue.issue_id}-${index}`}
                  style={{
                    borderBottom: "1px solid #e5e7eb",
                    opacity: isCompleted ? 0.6 : 1,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#f9fafb";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                  }}
                >
                  <td
                    style={{
                      border: "1px solid #e5e7eb",
                      padding: "6px 12px",
                      verticalAlign: "top",
                      color: "#374151",
                      fontSize: "0.75rem",
                    }}
                  >
                    <div
                      style={{
                        fontWeight: "500",
                        color: isCompleted ? "#10b981" : "#667eea",
                        background: isCompleted ? "#d1fae5" : "#eef2ff",
                        padding: "2px 8px",
                        borderRadius: "4px",
                        display: "inline-block",
                      }}
                    >
                      {issue.meta_id}
                    </div>
                  </td>
                  <td
                    style={{
                      border: "1px solid #e5e7eb",
                      padding: "6px 12px",
                      verticalAlign: "top",
                      color: "#374151",
                      fontSize: "0.75rem",
                    }}
                  >
                    {issueUrl ? (
                      <a
                        href={issueUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: "#3b82f6",
                          textDecoration: isCompleted ? "line-through" : "none",
                          fontSize: "0.75rem",
                          fontWeight: "500",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.textDecoration = "underline";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.textDecoration = isCompleted
                            ? "line-through"
                            : "none";
                        }}
                      >
                        {parsedValue.title || issue.name || "-"}
                      </a>
                    ) : (
                      <div
                        style={{
                          fontWeight: "500",
                          textDecoration: isCompleted ? "line-through" : "none",
                        }}
                      >
                        {parsedValue.title || issue.name || "-"}
                      </div>
                    )}
                  </td>
                  <td
                    style={{
                      border: "1px solid #e5e7eb",
                      padding: "6px 12px",
                      verticalAlign: "top",
                      color: "#374151",
                      fontSize: "0.75rem",
                    }}
                  >
                    {parsedValue.operating_result ? (
                      <div
                        style={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "pre-wrap",
                          lineHeight: "1.4",
                        }}
                      >
                        {parsedValue.operating_result}
                      </div>
                    ) : (
                      <div style={{ color: "#9ca3af" }}>-</div>
                    )}
                  </td>
                  <td
                    style={{
                      border: "1px solid #e5e7eb",
                      padding: "6px 12px",
                      verticalAlign: "top",
                      fontSize: "0.75rem",
                    }}
                  >
                    <div
                      style={{
                        fontWeight: "500",
                        color: statusColor.text,
                        background: statusColor.bg,
                        padding: "2px 8px",
                        borderRadius: "4px",
                        display: "inline-block",
                        fontSize: "0.7rem",
                      }}
                    >
                      {statusInfo?.displayName || "-"}
                    </div>
                  </td>
                  <td
                    style={{
                      border: "1px solid #e5e7eb",
                      padding: "6px 12px",
                      verticalAlign: "top",
                      color: "#6b7280",
                      fontSize: "0.7rem",
                    }}
                  >
                    {formatDate(issue.modified_at || issue.created_at)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};
